import Card from "@mui/material/Card";
import Box from "@mui/material/Box";
import CardContent from "@mui/material/CardContent";
import { Grid } from "@mui/material";
import Typography from "@mui/material/Typography";
import { red } from "@mui/material/colors";
import SectionTile from "./SectionTitle";
import APIEnquiry from "../../../types/APIEnquiry";
import ProtectedImageEmbed from "../../BusinessAttributes/ProtectedImageEmbed";
import APICompany from "../../../types/APICompany";

export default function WebPresenceResult({
  enquiry,
}: {
  enquiry: APIEnquiry;
}) {
  /*
   NOTE: For prototying purposes, I have copied the existing company response into the enquiry.
   This is *NOT* a commitment to maintain 1:1 schemas between web presence monitoring and the web presence review,
   instead, this is merely a shortcut for getting a reasonable quality demo put together quickly.  This is also
   not a commitment to *NOT* maintain a 1:1 match.
   */

  const summaryRemark = enquiry.remarks.find(
    (remark) => remark.subject === "summary" || !remark.subject
  );
  const healthRemarks = enquiry.remarks.filter(
    (remark) => remark.subject === "site_health"
  );
  const industryRemarks = enquiry.remarks.filter(
    (remark) => remark.subject === "industry"
  );
  const locationRemarks = enquiry.remarks.filter(
    (remark) => remark.subject === "location"
  );
  const reviewRemarks = enquiry.remarks.filter(
    (remark) => remark.subject === "reviews"
  );

  const site_is_up = summaryRemark?.message !== "The website is down.";
  const company: APICompany | null = summaryRemark?.data?.results?.evidence;
  const companyName = company?.name || "Unknown company name";
  const countries = getCountries(company);
  const reviewData = getReviewData(company);

  return (
    <Card sx={{ display: "flex", m: 1, pt: 1, pb: 1 }}>
      <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
        <CardContent sx={{ flex: "1 0 auto" }}>
          <Grid container spacing={2} marginTop={-2} columnSpacing={{ md: 6 }}>
            <Grid item xs={12}>
              <Typography component="div" variant="h5">
                {`${companyName} (${enquiry.domain})`}
              </Typography>
              <Typography
                variant="subtitle1"
                color={site_is_up ? "text.secondary" : red[900]}
                component="div"
              >
                {summaryRemark?.message}
              </Typography>
            </Grid>
            <SectionTile
              remarks={healthRemarks}
              alertLevel={
                site_is_up && healthRemarks.length === 0 ? "INFO" : "ERROR"
              }
              content={
                site_is_up
                  ? healthRemarks.length === 0
                    ? "The website is up and running and no issues were found with it's configuration."
                    : "The website is up and running."
                  : "The website is unreachable."
              }
              title="Site health"
            />
            <SectionTile
              remarks={industryRemarks}
              alertLevel={
                industryRemarks.length === 0
                  ? company?.industry?.primary_industry
                    ? "INFO"
                    : "WARN"
                  : "ERROR"
              }
              content={
                company?.industry?.primary_industry
                  ? `Primary industry: ${company.industry.primary_industry}`
                  : "Unknown primary industry"
              }
              title="Industry"
            />
            <SectionTile
              remarks={locationRemarks}
              alertLevel={
                locationRemarks.length === 0
                  ? !!countries && countries.length > 0
                    ? "INFO"
                    : "WARN"
                  : "ERROR"
              }
              content={
                countries.length > 0 ? (
                  countries.length === 1 ? (
                    countries[0]
                  ) : (
                    <ul>
                      {countries.map((country) => {
                        return <li key={country}>{country}</li>;
                      })}
                    </ul>
                  )
                ) : (
                  "Unable to determine location."
                )
              }
              title="Location"
            />
            <SectionTile
              remarks={reviewRemarks}
              alertLevel={
                reviewRemarks.length === 0
                  ? reviewData
                    ? "INFO"
                    : "WARN"
                  : "ERROR"
              }
              content={
                reviewData ? (
                  <ul>
                    <li>Review Count: {reviewData.count}</li>
                    <li>Review Count: {reviewData.score}</li>
                  </ul>
                ) : (
                  "Unable to find reviews."
                )
              }
              title="Reviews"
            />
          </Grid>
        </CardContent>
      </Box>
      {site_is_up && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "250px",
            height: "250px",
            "margin-top": "16px",
            "margin-bottom": "48px",
          }}
        >
          <ProtectedImageEmbed
            src={
              company?.domain.screenshot?.fullpage_image_link
                ? company.domain.screenshot.fullpage_image_link
                : null
            }
            relayQueryParameters={null}
            relayRequestId={null}
          />
        </Box>
      )}
    </Card>
  );
}

const getCountries = (company: APICompany | null): string[] => {
  const locations: string[] = [];
  if (!company) {
    return locations;
  }

  if (
    company.primary_address?.country &&
    !locations.includes(company.primary_address.country)
  ) {
    locations.push(company.primary_address.country);
  }

  if (
    company.website_traffic?.visits_per_month &&
    company.website_traffic.visits_per_month > 5000 &&
    company.website_traffic?.top_country_shares?.[0]
  ) {
    const country = company.website_traffic.top_country_shares[0].name;
    if (country && !locations.includes(country)) {
      locations.push(country);
    }
  }

  if (company.people && company.people.length > 0) {
    company.people
      .map((personData) => {
        return personData.country;
      })
      .forEach((country) => {
        if (country && !locations.includes(country)) {
          locations.push(country);
        }
      });
  }

  if (
    company.domain?.hosting_providers &&
    company.domain.hosting_providers.length > 0
  ) {
    company.domain.hosting_providers
      .map((providerData) => {
        return providerData.country;
      })
      .forEach((country) => {
        if (country && !locations.includes(country)) {
          locations.push(country);
        }
      });
  }

  if (
    company.domain.registrar?.country &&
    !locations.includes(company.domain.registrar.country)
  ) {
    locations.push(company.domain.registrar.country);
  }

  return locations;
};

const getReviewData = (
  company: APICompany | null
): { score: number; count: number } | null => {
  if (!company) {
    return null;
  }

  let score = 0.0;
  let count = 0;

  company.customer_reviews?.providers
    ?.filter((reviewData) => {
      return (
        reviewData.meta &&
        reviewData.meta.review_count &&
        reviewData.meta.review_average !== null
      );
    })
    ?.forEach((reviewData) => {
      score += reviewData.meta.review_count * reviewData.meta.review_average;
      count += reviewData.meta.review_count;
    });

  if (count) {
    const weightedAverage = parseFloat((score / count).toFixed(2));
    return { score: weightedAverage, count };
  }

  return null;
};
